import React from "react";
import { useAuth, useExcursion } from "contexts/api";
import type { ExcursionBooking } from "api/excursions/Excursion.typed";

function monthRangeKeys(month: Date): { from: string; to: string } {
  const y = month.getFullYear();
  const m = String(month.getMonth() + 1).padStart(2, "0");
  const from = `${y}-${m}-01`;
  const lastDay = new Date(y, month.getMonth() + 1, 0).getDate();
  const to = `${y}-${m}-${String(lastDay).padStart(2, "0")}`;
  return { from, to };
}

export function useExcursionBookingsForMonth() {
  const { listExcursionBookings, deleteExcursionBooking, updateExcursionBookingStatus } =
    useExcursion();
  const { refreshUsersMap } = useAuth();

  const [currentMonth, setCurrentMonth] = React.useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [bookings, setBookings] = React.useState<ExcursionBooking[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    void refreshUsersMap();
  }, [refreshUsersMap]);

  React.useEffect(() => {
    setLoading(true);
    const { from, to } = monthRangeKeys(currentMonth);
    // Omit scope so API applies planning.booking.* / planning.* visibility (me / company / all), same as calendar.
    listExcursionBookings({ from, to })
      .then((list) => setBookings(list ?? []))
      .finally(() => setLoading(false));
  }, [currentMonth, listExcursionBookings]);

  const setYearMonth = React.useCallback((year: number, month: number) => {
    const m = Math.min(12, Math.max(1, month));
    setCurrentMonth(new Date(year, m - 1, 1));
  }, []);

  const reload = React.useCallback(() => {
    setLoading(true);
    const { from, to } = monthRangeKeys(currentMonth);
    listExcursionBookings({ from, to })
      .then((list) => setBookings(list ?? []))
      .finally(() => setLoading(false));
  }, [currentMonth, listExcursionBookings]);

  return {
    currentMonth,
    bookings,
    loading,
    setYearMonth,
    reload,
    deleteExcursionBooking,
    updateExcursionBookingStatus,
  };
}
